import React, { useMemo } from 'react';
import { CommentSection, RatingCard, DoughnutGraph } from '../../organisms';
import { MetricCard, SpinnerDotted } from '../../atoms';
import { AttentionAlert, UserAlert } from '../../atoms/alerts';
import { commaingNumber } from '../../../../utils';
import WalletUtilizationCommon from './WalletUtilizationCommon';
import { UtilizationSankyChart } from '../../../tailwind/organisms';
import { Tooltip } from 'antd';
import { LodingSquareTabbar, PreferredPartner } from '../../molecules';
import { CommonSplitPlot } from '../../../opdClaims';
import { OverviewSectionIcon } from '../../../overview/OverviewSectionIcon';

const DoctorConsultation = ({ metricObj, usersObj, ratingObj, coverageObj, loading, benefitName = 'Consultations', selectedContractObj, utilizationObj, sqaureTabHandler }) => {
  const {
    healthCheckAvailedPercentage, successMetric, donutGraphData = [], partners = [], activeEmployeesAppointmentsCount, inactiveEmployeesAppointmentsCount,
    activeDependentsAppointmentsCount, inactiveDependentsAppointmentsCount, totalHealthChecksCompleted, unique_users, repeat_users, activeCustomerCount,
    activeEmployeesUpcomingAppointmentsCount, activeDependentsUpcomingAppointmentsCount, totalAmountSpent,
    activeUniqueEmployeesAppointmentsCount = 0, inactiveUniqueEmployeesAppointmentsCount = 0, hasCustomerFormSubmissions,
    activeUniqueDependentsAppointmentsCount = 0, inactiveUniqueDependentsAppointmentsCount = 0, donutBenefitGraphData = []
  } = metricObj;

  const { barlabels = [], bardata = [], barBgColors = [], xAxesLabel } = utilizationObj;
  const { ratingsPercent, totalratings, averagerating, commentSectionData = [] } = ratingObj;

  const { donutCoverageGraphData } = coverageObj;
  const currentYear = selectedContractObj?.is_running_contract

  const sankyChartData = useMemo(() => {
    const employeesAppointmentsCount = (activeEmployeesAppointmentsCount || 0) + (activeDependentsAppointmentsCount || 0)
    const dependentsAppointmentsCount = (inactiveEmployeesAppointmentsCount || 0) + (inactiveDependentsAppointmentsCount || 0)

    const totalUnique = (activeUniqueEmployeesAppointmentsCount || 0) + (inactiveUniqueEmployeesAppointmentsCount || 0) +
      (activeUniqueDependentsAppointmentsCount || 0) + (inactiveUniqueDependentsAppointmentsCount || 0)
    const uniqueActiveBranch = (activeUniqueEmployeesAppointmentsCount || 0) + (activeUniqueDependentsAppointmentsCount || 0)
    const uniqueInactiveBranch = (inactiveUniqueEmployeesAppointmentsCount || 0) + (inactiveUniqueDependentsAppointmentsCount || 0)

    const logValue = (v) => {
      const scaled = Math.log((v || 1) + 1)
      return Math.max(0.8, scaled)
    }
    const v24 = logValue(activeUniqueEmployeesAppointmentsCount)
    const v25 = logValue(activeUniqueDependentsAppointmentsCount)
    const v36 = logValue(inactiveUniqueEmployeesAppointmentsCount)
    const v37 = logValue(inactiveUniqueDependentsAppointmentsCount)

    const LINE_BREAK = '__BR__'
    // First node: total users + completed appointments
    const point1 = `${commaingNumber(totalUnique)} Users${LINE_BREAK}(Completed ${commaingNumber(totalHealthChecksCompleted || 0)} consultations)`
    // Second node: Active / Inactive users + completed appointments
    const point2 = `${commaingNumber(uniqueActiveBranch)} Active Users${LINE_BREAK}(Completed ${commaingNumber(employeesAppointmentsCount || 0)} consultations)`
    const point3 = `${commaingNumber(uniqueInactiveBranch)} Inactive Users${LINE_BREAK}(Completed ${commaingNumber(dependentsAppointmentsCount || 0)} consultations)`
    // Third node under Active: Employees / Dependents
    const point4 = `${commaingNumber(activeUniqueEmployeesAppointmentsCount || 0)} Employees${LINE_BREAK}(Completed ${commaingNumber(activeEmployeesAppointmentsCount || 0)} consultations)`
    const point5 = `${commaingNumber(activeUniqueDependentsAppointmentsCount || 0)} Dependents${LINE_BREAK}(Completed ${commaingNumber(activeDependentsAppointmentsCount || 0)} consultations)`
    // Third node under Inactive: Employees / Dependents (added space to force uniqueness)
    const point6 = `${commaingNumber(inactiveUniqueEmployeesAppointmentsCount || 0)} Employees ${LINE_BREAK}  (Completed ${commaingNumber(inactiveEmployeesAppointmentsCount || 0)} consultations)`
    const point7 = `${commaingNumber(inactiveUniqueDependentsAppointmentsCount || 0)} Dependents ${LINE_BREAK}(Completed ${commaingNumber(inactiveDependentsAppointmentsCount || 0)} consultations)`

    const data = {
      nodes: [{ id: point1 }, { id: point2 },
              { id: point3 }, { id: point4 },
              { id: point5 }, { id: point6 },
              { id: point7 }],
      links: [
        { source: point1, target: point2, value: v24 + v25 },
        { source: point1, target: point3, value: v36 + v37 },
        { source: point2, target: point4, value: v24 },
        { source: point2, target: point5, value: v25 },
        { source: point3, target: point6, value: v36 },
        { source: point3, target: point7, value: v37 }
      ]
    }

    const colors = ['#0951E1', '#14866D', '#C54B4F', '#1FBDC0', '#5163BA', '#2563EB', '#8895AF']
    return { data, colors }
  }, [
    totalHealthChecksCompleted,
    activeEmployeesAppointmentsCount, inactiveEmployeesAppointmentsCount, activeDependentsAppointmentsCount, inactiveDependentsAppointmentsCount,
    activeUniqueEmployeesAppointmentsCount, inactiveUniqueEmployeesAppointmentsCount,
    activeUniqueDependentsAppointmentsCount, inactiveUniqueDependentsAppointmentsCount
  ])

  const formattedDistributionData = useMemo(() => {
    const formatted = {};
    barlabels.forEach((label, index) => {
      const val = bardata[index];
      const volume = typeof val === 'object' ? val.t : val;
      const key = xAxesLabel === 'Age' ? `${label} age group` : label;
      formatted[key] = {
        volume: volume,
        amount: 0 
      };
    });
    return formatted;
  }, [barlabels, bardata, xAxesLabel]);

  const tooltipText = (selfCount = 0, dependentCount = 0) => {
    return (
      <div>
        {employeeData('Self consultations', selfCount)}
        {employeeData('Dependents consultations', dependentCount)}
        <hr className='my-3' />
        {employeeData('Total consultations', selfCount + dependentCount)}
      </div>
    )
  }

  const employeeData = (text, count) => {
    return (
      <div className='flex justify-between'>
        <span className='font-semibold'>{text}</span>
        <span className='font-semibold'>{commaingNumber(count)}</span>
      </div>
    )
  }

  const metricValueSuffix = (selfCount, dependentCount, benefitName='consultation') => {
    return (
      <span >
        <span className='font-semibold text-gray-800'>
        Upcoming {benefitName}
        </span>
        <Tooltip placement='bottom' title={tooltipText(selfCount, dependentCount)} overlayStyle={{width: '16rem'}}>
          {/* <img src={info_icon} className='h-3 w-3 ml-1 inline cursor-pointer' alt='info_icon' /> */}
        </Tooltip>
      </span>
    )
  }

  return (
    <div>
      {(!loading && currentYear && healthCheckAvailedPercentage < successMetric) &&
        <AttentionAlert
          title='Attention Required'
          description={
            <span>
              Utilization for your organization is low as per the success metrics set for your organization. To improve
              the utilization please reach out to your{' '}
              <span className='text-blue-500 font-semibold'>
                customer success manager.
              </span>
            </span>
          }
          classes='mt-6'
        />}
      <p className='mt-8 text-xs font-semibold uppercase tracking-wider text-gray-500'>Sponsored {benefitName} summary</p>
      { !loading ?
        <>
          <div className='mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3'>
            <MetricCard
              variant='ds-default'
              icon={<OverviewSectionIcon name='wallet' />}
              loading={loading}
              loaderHeight={120}
              title={`Total amount spent on sponsored ${benefitName}`}
              subTitle='Calculated based on completed consultations only'
              metricValue={`₹${commaingNumber(totalAmountSpent)}`}
              alertText='*Does not include amounts for Online GP Consultation'
              alertClasses='px-4'
              classes='min-w-0 flex-1'
            />
            <MetricCard
              variant='ds-default'
              icon={<OverviewSectionIcon name='consultations' />}
              loading={loading}
              loaderHeight={120}
              title={`Completed ${benefitName}`}
              subTitle='Completed (unique) consultations for self and dependents'
              metricValue={commaingNumber(totalHealthChecksCompleted)}
              metricValueSuffix='consultations'
              classes='min-w-0 flex-1'
            />
            <MetricCard
              variant='ds-default'
              icon={<OverviewSectionIcon name='upcoming_appointments' />}
              loading={loading}
              loaderHeight={120}
              title={metricValueSuffix(activeEmployeesUpcomingAppointmentsCount, activeDependentsUpcomingAppointmentsCount, benefitName)}
              subTitle='Upcoming (unique) consultations for self and dependents'
              metricValue={commaingNumber(activeEmployeesUpcomingAppointmentsCount + activeDependentsUpcomingAppointmentsCount)}
              metricValueSuffix='consultations'
              classes='min-w-0 flex-1'
            />
          </div>
          <div>
            <p className='mt-10 text-base font-semibold text-gray-900'>User-wise consultations breakdown</p>
            <UtilizationSankyChart
              sankeyData={sankyChartData}
              classes='mt-4 rounded-xl border border-gray-200 bg-white px-6 py-10 shadow-sm md:px-9 md:py-14'
              height={450}
            />
          </div>
        </> :
        <div className='h-40 flex items-center justify-center'><SpinnerDotted /></div>
      }

      <WalletUtilizationCommon metricObj={metricObj} loading={loading} benefitName={benefitName} uniqueUsers={unique_users} activeCustomerCount={activeCustomerCount}/>

      <div className='mt-12 flex flex-col gap-8 lg:flex-row lg:justify-between'>
        <div className='flex min-w-0 flex-1 flex-col lg:mr-4 lg:w-1/2'>
          <p className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>{`${benefitName} completion types`}</p>
          <div className='mb-3 text-sm text-gray-600'>
            Distribution of completed {benefitName} consultations by type
          </div>
          <DoughnutGraph
            loading={loading}
            loaderHeight={200}
            title={benefitName}
            graphData={donutBenefitGraphData}
            classes='h-full'
            heightOfLoader={200}
            id={'consultation'}
            hideBrackets={true}
          />
        </div>
        <div className='flex min-w-0 flex-1 flex-col lg:w-1/2'>
          <p className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>Top preferred specialities</p>
          <div className='mb-3 text-sm text-gray-600'>
            Ranks specialities based on no. of employees who selected them for completing their consultations
          </div>
          <PreferredPartner loading={loading} loaderHeight={200} list={partners} header='Specialities' classes='h-full' />
        </div>
      </div>
      {hasCustomerFormSubmissions &&
        <p className='text-sm text-gray-600 mt-2 mb-0 italic leading-none'>
          *Also includes data from the form submitted by the customer
        </p>
      }

      <div className='mt-12 flex flex-col gap-8 lg:flex-row'>
        <div className='flex flex-col pr-4 lg:w-2/4'>
          <div className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>Ratings and reviews</div>
          <div className='mb-3 text-sm text-gray-600'>
            These ratings are captured after completing the consultations.
          </div>
          <RatingCard
            loading={loading}
            loaderHeight={200}
            rating={averagerating}
            number_of_ratings={totalratings}
            five_star_rating={ratingsPercent && ratingsPercent[4]}
            four_star_rating={ratingsPercent && ratingsPercent[3]}
            three_star_rating={ratingsPercent && ratingsPercent[2]}
            two_star_rating={ratingsPercent && ratingsPercent[1]}
            one_star_rating={ratingsPercent && ratingsPercent[0]}
            classes='h-full'
          />
        </div>

        <div className='flex w-full flex-col lg:w-2/4'>
          <div className='mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>Top comments</div>
          <div className='mb-3 text-sm text-gray-600'>
            These reviews are captured after completing the consultations.
          </div>
          <CommentSection loading={loading} loaderHeight={200} points={commentSectionData} classes='h-full' />
        </div>
      </div>

      {/* Employee utilisation graph   */}

      <div className='mt-12'>
        <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>Employee profile distribution</div>
          <div className='my-2 text-sm text-gray-600'>
            Share of employees (who completed at least one consultation) by department, location, age group, and gender
          </div>
        <LodingSquareTabbar
          loading={loading}
          loaderHeight={25}
          list={['Departments', 'Location', 'Age', 'Gender']}
          handler={sqaureTabHandler}
          classes='mt-3'
        />
        <div className='mt-5'>
          {bardata && bardata.length === 0 ? (
            <div className='rounded-xl border border-gray-200 bg-white p-6 shadow-sm'>
              <UserAlert
                para={`${xAxesLabel} data is not available`}
                paraClasses=' font-semibold text-gray-600'
                imgClasses=' h-5 w-5'
                classes='p-4'
              />
            </div>
          ) : (
            <CommonSplitPlot
              data={formattedDistributionData}
              title={xAxesLabel}
              loading={loading}
              initialMetric='volume'
              hideToggle={true}
              hideSummary={true}
              hideAov={true}
              hideImage={true}
              precision={2}
              shareLabel='%Share'
              hideBrackets={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DoctorConsultation;
